import { clsx } from 'clsx';

type PositionStatus = 'watching' | 'open' | 'partial' | 'closed' | 'dismissed' | 'expired';

const STATUS_COLOR: Record<PositionStatus, string> = {
  watching: 'text-blue-400',
  open: 'text-accent',
  partial: 'text-warning',
  closed: 'text-loss',
  dismissed: 'text-text-muted',
  expired: 'text-loss',
};

/**
 * Position status label used by the plays table and the trader detail page.
 * Renders as uppercase mono text in a status-specific colour. The DB value
 * `partial` is shown as TRIM (with optional %) so the language matches the
 * trader's verb instead of the enum.
 *
 * CLOSED is coloured by realized P/L when known — green for a gain, red for a
 * loss — so a winning close never reads as red. Neutral grey when P/L unknown.
 */
export function PositionStatusLabel({
  status,
  remainingPct,
  pnlPercent,
}: {
  status: PositionStatus;
  remainingPct?: number;
  pnlPercent?: number | null;
}) {
  // DB enum vs UI label aliases:
  //   partial  → TRIM (matches the trader's verb)
  //   watching → WATCH (shorter, matches the action verb)
  const label =
    status === 'partial' ? 'trim' :
    status === 'watching' ? 'watch' :
    status;

  const colorClass =
    status === 'closed'
      ? pnlPercent == null
        ? 'text-text-secondary'
        : pnlPercent >= 0
          ? 'text-profit'
          : 'text-loss'
      : STATUS_COLOR[status];

  return (
    <span className={clsx('text-xs font-mono uppercase', colorClass)}>
      {label}
      {status === 'partial' && remainingPct !== undefined && ` ${remainingPct}%`}
    </span>
  );
}
